import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Divider,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  Today as TodayIcon,
  EventAvailable as EventAvailableIcon,
  AccessTime as TimeIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { darkColors, lightColors } from '../theme';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { createCommonStyles } from '../theme/styles';
import { colors } from '../theme/colors';
import appointmentService from '../services/appointmentService';

const LiveDashboard = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const { isDarkMode } = useAppTheme();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const customColors = isDarkMode ? darkColors : lightColors;
  const commonStyles = createCommonStyles(isDarkMode);
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [nextAppointment, setNextAppointment] = useState(null);
  const [stats, setStats] = useState({
    completedToday: 0,
    totalToday: 0,
    revenue: 0
  });
  const [loading, setLoading] = useState(true);

  // Helper funkcija za dobijanje tenantId
  const getCurrentTenantId = useCallback(() => {
    if (currentUser?.tenantId) {
      return currentUser.tenantId?._id || currentUser.tenantId;
    }
    
    if (currentUser?.tenantConnections && currentUser.tenantConnections.length > 0) {
      const firstConnection = currentUser.tenantConnections[0];
      return firstConnection?.tenantId?._id || firstConnection?.tenantId;
    }
    
    return null;
  }, [currentUser]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Detektuj F11 za fullscreen i sakrij sidebar/navbar
  useEffect(() => {
    const checkAndUpdateFullscreen = () => {
      // Proveri SAMO Fullscreen API - ne proveravamo dimenzije jer su nepouzdane
      const isInFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      
      // Proveri da li su dimenzije približne screen dimenzijama (za F11)
      // Koristimo VEOMA strožu proveru (99%) da se osiguramo da je stvarno fullscreen
      const screenHeight = window.screen.availHeight;
      const screenWidth = window.screen.availWidth;
      const innerHeight = window.innerHeight;
      const innerWidth = window.innerWidth;
      const heightRatio = innerHeight / screenHeight;
      const widthRatio = innerWidth / screenWidth;
      
      // F11 fullscreen - proveri da li je window VELIKI i blizu screen dimenzijama
      // Moraju biti OBA uslova ispunjena: visina >= 99% I širina >= 99%
      const isF11Fullscreen = heightRatio >= 0.99 && widthRatio >= 0.99 && 
                              innerHeight > 800 && innerWidth > 1200; // Dodatna provera da nije mali prozor
      
      if (isInFullscreen || isF11Fullscreen) {
        localStorage.setItem('hideSidebarNavbar', 'true');
        window.dispatchEvent(new CustomEvent('fullscreenToggle'));
      } else {
        // Ako nije u fullscreen, OBVEZNO ukloni flag
        localStorage.removeItem('hideSidebarNavbar');
        window.dispatchEvent(new CustomEvent('fullscreenToggle'));
      }
    };

    const handleKeyDown = (e) => {
      // Detektuj F11 pritisak
      if (e.key === 'F11' || e.keyCode === 122) {
        // Sačekaj da browser završi fullscreen akciju
        setTimeout(checkAndUpdateFullscreen, 400);
      }
      
      // ESC key - izađi iz fullscreen i prikaži sidebar/navbar
      if (e.key === 'Escape') {
        // Proveri da li je stvarno izašao iz fullscreen
        setTimeout(() => {
          const isInFullscreen = !!(
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.mozFullScreenElement ||
            document.msFullscreenElement
          );
          if (!isInFullscreen) {
            checkAndUpdateFullscreen();
          }
        }, 150);
      }
    };

    const handleResize = () => {
      // Proveri fullscreen stanje kada se prozor promeni
      clearTimeout(window.liveDashboardFullscreenCheck);
      window.liveDashboardFullscreenCheck = setTimeout(checkAndUpdateFullscreen, 200);
    };

    // Listeners za Fullscreen API
    const handleFullscreenChange = () => {
      checkAndUpdateFullscreen();
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleResize);

    // Resetuj flag na početku - ne pretpostavljaj da je u fullscreen
    localStorage.removeItem('hideSidebarNavbar');
    
    // NE proveravamo initial state automatski na mount-u
    // Layout komponenta će resetovati state, a mi ćemo proveravati samo na event-ima
    // Ovo sprečava da se sidebar/navbar sakriju kada se vratiš na live-dashboard

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleResize);
      if (window.liveDashboardFullscreenCheck) {
        clearTimeout(window.liveDashboardFullscreenCheck);
      }
      // Resetuj flag kada se komponenta unmount-uje
      localStorage.removeItem('hideSidebarNavbar');
      // Trigger event da Layout komponenta zna da treba da prikaže sidebar/navbar
      window.dispatchEvent(new CustomEvent('fullscreenToggle'));
    };
  }, []);

  // Fetch today's appointments
  const fetchTodaysAppointments = useCallback(async () => {
    try {
      setLoading(true);
      const tenantId = getCurrentTenantId();
      
      if (!tenantId) {
        setTodaysAppointments([]);
        setNextAppointment(null);
        setStats({ completedToday: 0, totalToday: 0, revenue: 0 });
        setLoading(false);
        return;
      }

      // Get today's date in format YYYY-MM-DD (local format)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`;

      // Fetch appointments (bez dateFrom/dateTo filtera, filtriramo na frontendu)
      let response;
      if (currentUser?.userType === 'super_admin' && !tenantId) {
        // Super admin bez izabranog tenanta - ne prikazuj ništa
        setTodaysAppointments([]);
        setNextAppointment(null);
        setStats({ completedToday: 0, totalToday: 0, revenue: 0 });
        setLoading(false);
        return;
      } else if (currentUser?.userType === 'super_admin' && tenantId) {
        // Super admin sa izabranim tenantom
        response = await appointmentService.getAppointmentsByTenant(tenantId, 1, 1000, {
          sortBy: 'appointmentDate',
          sortOrder: 'asc'
        });
      } else {
        // Ostali korisnici
        response = await appointmentService.getAppointmentsByTenant(tenantId, 1, 1000, {
          sortBy: 'appointmentDate',
          sortOrder: 'asc'
        });
      }

      // Backend vraća podatke direktno (nije u response.data)
      const appointments = response.appointments || response.data?.appointments || response.data || response || [];
      
      // Filter appointments for today only using string comparison, excluding cancelled
      const todayAppointments = appointments.filter(apt => {
        if (!apt.appointmentDate) return false;
        
        // Exclude cancelled appointments
        if (apt.status === 'cancelled' || apt.status === 'otkazano') return false;
        
        // Convert appointment date to YYYY-MM-DD format for comparison
        const aptDate = new Date(apt.appointmentDate);
        const aptYear = aptDate.getFullYear();
        const aptMonth = String(aptDate.getMonth() + 1).padStart(2, '0');
        const aptDay = String(aptDate.getDate()).padStart(2, '0');
        const aptDateStr = `${aptYear}-${aptMonth}-${aptDay}`;
        
        return aptDateStr === todayStr;
      });

      // Format appointments for display and calculate if completed based on current time
      const now = new Date();
      const formattedAppointments = todayAppointments.map(apt => {
        const timeStr = apt.selectedTime || '';
        
        const customerName = apt.isQuickAppointment && apt.customerName
          ? apt.customerName
          : apt.customerId
          ? typeof apt.customerId === 'object'
            ? `${apt.customerId.firstName || ''} ${apt.customerId.lastName || ''}`.trim()
            : 'N/A'
          : 'N/A';

        const employeeName = apt.employeeId
          ? typeof apt.employeeId === 'object'
            ? `${apt.employeeId.firstName || ''} ${apt.employeeId.lastName || ''}`.trim()
            : 'N/A'
          : 'N/A';

        const services = apt.serviceIds && apt.serviceIds.length > 0
          ? apt.serviceIds.map(s => typeof s === 'object' ? s.name : s).join(', ')
          : 'Nema usluga';

        // Get duration from main object first, fallback to calculating from services
        const duration = Number(apt.duration) || Number(apt.totalDuration) || 
          (apt.serviceIds && apt.serviceIds.length > 0
            ? apt.serviceIds.reduce((sum, s) => {
                const serviceDuration = typeof s === 'object' ? (Number(s.duration) || 0) : 0;
                return sum + serviceDuration;
              }, 0)
            : 0);

        // Calculate if appointment is completed or in progress based on current time
        let calculatedStatus = apt.status || 'pending';
        
        // Only calculate if status is not cancelled and we have a selectedTime
        if (calculatedStatus !== 'cancelled' && calculatedStatus !== 'otkazano' && timeStr) {
          // Parse appointment time
          const [hours, minutes] = timeStr.split(':').map(Number);
          if (!isNaN(hours) && !isNaN(minutes)) {
            const appointmentStartTime = new Date(today);
            appointmentStartTime.setHours(hours, minutes, 0, 0);
            
            // Calculate appointment end time (start + duration in minutes)
            const appointmentEndTime = new Date(appointmentStartTime);
            appointmentEndTime.setMinutes(appointmentEndTime.getMinutes() + duration);
            
            // Check if appointment is currently in progress
            if (now >= appointmentStartTime && now < appointmentEndTime) {
              calculatedStatus = 'in_progress';
            }
            // If current time is after appointment end time, mark as completed
            else if (now >= appointmentEndTime) {
              calculatedStatus = 'completed';
            }
          }
        }

        return {
          id: apt._id,
          _id: apt._id,
          time: timeStr,
          customer: customerName,
          service: services,
          employee: employeeName,
          status: calculatedStatus,
          originalStatus: apt.status || 'pending', // Store original status from API
          appointmentDate: apt.appointmentDate,
          selectedTime: apt.selectedTime,
          totalPrice: apt.totalPrice || apt.calculatedTotalPrice || 0,
          duration: duration
        };
      });

      // Find next appointment (first that starts after current time and is not completed/cancelled/in_progress)
      const nextApt = formattedAppointments.find(apt => {
        // Skip cancelled, completed, and in-progress appointments
        if (apt.status === 'cancelled' || apt.status === 'otkazano' || apt.status === 'completed' || apt.status === 'in_progress') {
          return false;
        }
        
        // Skip if no time is set
        if (!apt.time) {
          return false;
        }
        
        const [hours, minutes] = apt.time.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) {
          return false;
        }
        
        const aptStartTime = new Date(today);
        aptStartTime.setHours(hours, minutes, 0, 0);
        
        // Check if appointment starts in the future (current time < appointment start time)
        return now < aptStartTime;
      });

      // Set next appointment
      if (nextApt) {
        setNextAppointment({
          id: nextApt._id,
          customerName: nextApt.customer,
          time: nextApt.time,
          service: nextApt.service.split(',')[0] || nextApt.service, // First service or all
          duration: nextApt.duration || 0,
          employee: nextApt.employee,
          avatar: nextApt.customer.charAt(0).toUpperCase(),
          status: nextApt.status
        });
      } else {
        setNextAppointment(null);
      }

      // Calculate stats
      const completed = formattedAppointments.filter(apt => apt.status === 'completed').length;
      const total = formattedAppointments.filter(apt => apt.status !== 'cancelled').length;
      const revenue = formattedAppointments
        .filter(apt => apt.status === 'completed')
        .reduce((sum, apt) => sum + (Number(apt.totalPrice) || 0), 0);

      setStats({
        completedToday: completed,
        totalToday: total,
        revenue: revenue
      });

      // Sort appointments by time and set
      const sortedAppointments = formattedAppointments.sort((a, b) => {
        // Handle missing times - put them at the end
        if (!a.time || !b.time) {
          if (!a.time && !b.time) return 0;
          if (!a.time) return 1;
          if (!b.time) return -1;
        }
        
        const [aHours, aMins] = a.time.split(':').map(Number);
        const [bHours, bMins] = b.time.split(':').map(Number);
        
        // If parsing failed, put at end
        if (isNaN(aHours) || isNaN(aMins)) return 1;
        if (isNaN(bHours) || isNaN(bMins)) return -1;
        
        const aTime = aHours * 60 + aMins;
        const bTime = bHours * 60 + bMins;
        return aTime - bTime;
      });

      setTodaysAppointments(sortedAppointments);
    } catch (error) {
      console.error('Error fetching today\'s appointments:', error);
      setTodaysAppointments([]);
      setNextAppointment(null);
      setStats({ completedToday: 0, totalToday: 0, revenue: 0 });
    } finally {
      setLoading(false);
    }
  }, [getCurrentTenantId, currentUser]);

  // Load appointments on mount and when tenant changes
  // Refresh data from API every 2 minutes to get new appointments
  useEffect(() => {
    fetchTodaysAppointments();
    
    // Refresh data from API every 2 minutes (to get new appointments)
    const interval = setInterval(() => {
      fetchTodaysAppointments();
    }, 120000); // 2 minutes = 120000ms

    return () => clearInterval(interval);
  }, [fetchTodaysAppointments]);

  // Update appointment statuses every second based on current time
  useEffect(() => {
    setTodaysAppointments(prevAppointments => {
      if (prevAppointments.length === 0) return prevAppointments;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const updatedAppointments = prevAppointments.map(apt => {
        // Skip if cancelled - keep original status
        if (apt.status === 'cancelled' || apt.status === 'otkazano') {
          return apt;
        }

        // Get original status from API (if stored) or use current status
        const originalStatus = apt.originalStatus || apt.status;
        // For calculated statuses (completed/in_progress), use original non-calculated status
        const baseStatus = (originalStatus === 'completed' || originalStatus === 'in_progress') 
          ? 'confirmed' 
          : originalStatus;

        // Skip if no time is set
        if (!apt.time) {
          return apt;
        }

        // Parse appointment time
        const [hours, minutes] = apt.time.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) {
          return apt;
        }
        
        const appointmentStartTime = new Date(today);
        appointmentStartTime.setHours(hours, minutes, 0, 0);
        
        // Get duration - check multiple possible fields and calculate from services if needed
        let durationMinutes = Number(apt.duration) || Number(apt.totalDuration) || 0;
        
        // If duration is 0, try to calculate from services
        if (durationMinutes === 0 && apt.serviceIds && apt.serviceIds.length > 0) {
          durationMinutes = apt.serviceIds.reduce((sum, s) => {
            const serviceDuration = typeof s === 'object' ? (Number(s.duration) || 0) : 0;
            return sum + serviceDuration;
          }, 0);
        }
        
        // Calculate appointment end time (start + duration in minutes)
        const appointmentEndTime = new Date(appointmentStartTime);
        appointmentEndTime.setMinutes(appointmentEndTime.getMinutes() + durationMinutes);
        
        let calculatedStatus = baseStatus;
        
        // Check if appointment is currently in progress (use getTime() for accurate comparison)
        const currentTimeMs = currentTime.getTime();
        const startTimeMs = appointmentStartTime.getTime();
        const endTimeMs = appointmentEndTime.getTime();
        
        if (currentTimeMs >= startTimeMs && currentTimeMs < endTimeMs) {
          calculatedStatus = 'in_progress';
        }
        // If current time is after appointment end time, mark as completed
        else if (currentTimeMs >= endTimeMs) {
          calculatedStatus = 'completed';
        }

        // Only update if status changed
        if (calculatedStatus !== apt.status) {
          return { ...apt, status: calculatedStatus };
        }
        
        return apt;
      });

      // Update next appointment based on updated appointments
      const todayForNext = new Date();
      todayForNext.setHours(0, 0, 0, 0);
      
      const nextApt = updatedAppointments.find(apt => {
        if (apt.status === 'cancelled' || apt.status === 'otkazano' || apt.status === 'completed' || apt.status === 'in_progress') {
          return false;
        }
        
        // Skip if no time is set
        if (!apt.time) {
          return false;
        }
        
        const [hours, minutes] = apt.time.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) {
          return false;
        }
        
        const aptStartTime = new Date(todayForNext);
        aptStartTime.setHours(hours, minutes, 0, 0);
        return currentTime < aptStartTime;
      });

      setNextAppointment(prevNext => {
        if (nextApt) {
          if (!prevNext || prevNext.id !== nextApt._id) {
            return {
              id: nextApt._id,
              customerName: nextApt.customer,
              time: nextApt.time,
              service: nextApt.service.split(',')[0] || nextApt.service,
              duration: nextApt.duration || 0,
              employee: nextApt.employee,
              avatar: nextApt.customer.charAt(0).toUpperCase(),
              status: nextApt.status
            };
          }
          return prevNext;
        } else {
          return null;
        }
      });

      // Update stats based on updated appointments
      const completed = updatedAppointments.filter(apt => apt.status === 'completed').length;
      const total = updatedAppointments.filter(apt => apt.status !== 'cancelled' && apt.status !== 'otkazano').length;
      const revenue = updatedAppointments
        .filter(apt => apt.status === 'completed')
        .reduce((sum, apt) => sum + (Number(apt.totalPrice) || 0), 0);

      setStats({
        completedToday: completed,
        totalToday: total,
        revenue: revenue
      });

      return updatedAppointments;
    });
  }, [currentTime]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return colors.status.appointment.completed;
      case 'in_progress': return '#FFA500'; // Orange color for in progress
      case 'confirmed': return colors.status.appointment.confirmed;
      case 'pending': return colors.status.appointment.pending;
      case 'cancelled':
      case 'otkazano': return colors.status.appointment.cancelled;
      default: return customColors.text.muted;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Završeno';
      case 'in_progress': return 'U obradi';
      case 'confirmed': return 'Potvrđeno';
      case 'pending': return 'Na čekanju';
      case 'cancelled':
      case 'otkazano': return 'Otkazano';
      default: return status;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: isDarkMode 
        ? customColors.background.main
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      p: 3,
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decorative elements */}
      <Box sx={{
        position: 'absolute',
        top: -100,
        right: -100,
        width: 300,
        height: 300,
        borderRadius: '50%',
        background: isDarkMode ? customColors.background.glass : 'rgba(6, 182, 212, 0.05)',
        zIndex: 0
      }} />
      <Box sx={{
        position: 'absolute',
        bottom: -150,
        left: -150,
        width: 400,
        height: 400,
        borderRadius: '50%',
        background: isDarkMode ? customColors.background.glass : 'rgba(124, 58, 237, 0.03)',
        zIndex: 0
      }} />

      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header - Naslov i vreme u jednom redu */}
        {!isMobile && (
          <Box sx={{ 
            mb: 4, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h3" sx={commonStyles.textHi}>
                Današnji raspored
              </Typography>
              <Typography variant="h6" sx={{ ...commonStyles.textMed, mt: 0.5, fontWeight: 300 }}>
                {currentTime.toLocaleDateString('sr-RS', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="h4" sx={{ 
                ...commonStyles.textHi,
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '0.05em'
              }}>
                {currentTime.toLocaleTimeString('sr-RS')}
              </Typography>
            </Box>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* Next Appointment - Large Card */}
            <Grid item xs={12} lg={7}>
              <Card 
                elevation={3}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                      <ScheduleIcon sx={{ fontSize: 20 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                      Sledeći termin
                    </Typography>
                  </Stack>

                  {nextAppointment ? (
                    <>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <CalendarIcon color="action" fontSize="small" />
                          <Typography variant="body2" fontWeight="medium">
                            {currentTime.toLocaleDateString('sr-RS', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TimeIcon color="primary" fontSize="small" />
                          <Typography variant="body1" fontWeight="medium" color="primary">
                            {nextAppointment.time}
                          </Typography>
                        </Stack>
                      </Stack>
                      <Divider sx={{ my: 1.5 }} />
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                          <Avatar sx={{ 
                            bgcolor: 'primary.main',
                            width: 48,
                            height: 48,
                            fontSize: '1.25rem',
                            fontWeight: 600
                          }}>
                            {nextAppointment.avatar}
                          </Avatar>
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {nextAppointment.customerName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {nextAppointment.employee}
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, color: 'text.secondary', display: 'block' }}>
                          Usluge:
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <TimeIcon color="action" fontSize="small" />
                          <Typography variant="body2" fontWeight="medium">
                            {nextAppointment.service}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            ({nextAppointment.duration} min)
                          </Typography>
                        </Stack>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                        <Chip 
                          label={getStatusText(nextAppointment.status)}
                          size="small"
                          sx={{
                            background: getStatusColor(nextAppointment.status),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      minHeight: 120,
                      color: 'text.secondary'
                    }}>
                      <Typography variant="body2" textAlign="center">
                        Nema zakazanih termina za danas
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

          {/* Stats Cards - Horizontalno u jednom redu */}
          <Grid item xs={12} lg={5}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box sx={{
                  ...commonStyles.glassSurface,
                  ...commonStyles.gradientStroke,
                  p: 1,
                  borderRadius: 3,
                  transition: "transform .22s ease, box-shadow .22s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: colors.shadows.card,
                  },
                }}>
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Box sx={commonStyles.accentBadge(customColors.success.main)}>
                      <CheckCircleIcon sx={{ fontSize: 24, color: customColors.success.main }} />
                    </Box>
                    <Typography variant="h5" sx={{
                      fontWeight: 900,
                      letterSpacing: "-0.02em",
                      background: "linear-gradient(90deg, #fff 0%, #3B82F6 30%, #1D4ED8 60%, #06B6D4 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textShadow: colors.shadows.text,
                      mt: 0.25
                    }}>
                      {stats.completedToday}
                    </Typography>
                    <Typography variant="caption" sx={{ ...commonStyles.textMed, mt: 0.25, fontSize: '0.65rem' }}>
                      Završeno
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{
                  ...commonStyles.glassSurface,
                  ...commonStyles.gradientStroke,
                  p: 1,
                  borderRadius: 3,
                  transition: "transform .22s ease, box-shadow .22s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: colors.shadows.card,
                  },
                }}>
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Box sx={commonStyles.accentBadge(customColors.primary.main)}>
                      <TodayIcon sx={{ fontSize: 24, color: customColors.primary.main }} />
                    </Box>
                    <Typography variant="h5" sx={{
                      fontWeight: 900,
                      letterSpacing: "-0.02em",
                      background: "linear-gradient(90deg, #fff 0%, #3B82F6 30%, #1D4ED8 60%, #06B6D4 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textShadow: colors.shadows.text,
                      mt: 0.25
                    }}>
                      {stats.totalToday}
                    </Typography>
                    <Typography variant="caption" sx={{ ...commonStyles.textMed, mt: 0.25, fontSize: '0.65rem' }}>
                      Ukupno
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box sx={{
                  ...commonStyles.glassSurface,
                  ...commonStyles.gradientStroke,
                  p: 1,
                  borderRadius: 3,
                  transition: "transform .22s ease, box-shadow .22s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: colors.shadows.card,
                  },
                }}>
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                    <Box sx={commonStyles.accentBadge(customColors.warning.main)}>
                      <TrendingUpIcon sx={{ fontSize: 24, color: customColors.warning.main }} />
                    </Box>
                    <Typography variant="h6" sx={{
                      fontWeight: 900,
                      letterSpacing: "-0.02em",
                      background: "linear-gradient(90deg, #fff 0%, #3B82F6 30%, #1D4ED8 60%, #06B6D4 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textShadow: colors.shadows.text,
                      mt: 0.25,
                      fontSize: '1rem'
                    }}>
                      {stats.revenue.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ ...commonStyles.textMed, mt: 0.25, fontSize: '0.65rem' }}>
                      RSD
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* Today's Schedule */}
          <Grid item xs={12} lg={7}>
            <Card 
              elevation={3}
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                    <EventAvailableIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" color="primary">
                    Raspored za danas
                  </Typography>
                </Stack>

                {todaysAppointments.length === 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: 120,
                    color: 'text.secondary'
                  }}>
                    <Typography variant="body2" textAlign="center">
                      Nema zakazanih termina za danas
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {todaysAppointments.map((appointment, index) => (
                      <Box key={appointment.id}>
                        <Stack 
                          direction={isMobile ? "column" : "row"} 
                          alignItems={isMobile ? "flex-start" : "center"} 
                          justifyContent="space-between" 
                          spacing={1.5}
                          sx={{ py: 1 }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ minWidth: 70, textAlign: 'center' }}>
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                                <TimeIcon color="primary" fontSize="small" />
                                <Typography variant="body2" fontWeight="medium" color="primary">
                                  {appointment.time}
                                </Typography>
                              </Stack>
                            </Box>
                            <Avatar sx={{ 
                              background: getStatusColor(appointment.status),
                              width: 36,
                              height: 36,
                              fontSize: '0.875rem'
                            }}>
                              {appointment.customer.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="medium" sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mb: 0.25
                              }}>
                                {appointment.customer}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}>
                                {appointment.service}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {appointment.employee}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip 
                            label={getStatusText(appointment.status)}
                            size="small"
                            sx={{
                              background: getStatusColor(appointment.status),
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                              height: '22px'
                            }}
                          />
                        </Stack>
                        {index < todaysAppointments.length - 1 && <Divider sx={{ my: 0.75 }} />}
                      </Box>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        )}
      </Box>
    </Box>
  );
};

export default LiveDashboard;
